import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, LockKeyhole, MailCheck } from 'lucide-react';
import Logo from '../../components/Logo';
import { useAuth } from '../../context/AuthContext';
import { firebaseReady } from '../../lib/firebase';
import { ADMIN_EMAILS } from '../../lib/firebase-config';

export default function AdminLogin() {
  const { user, isStaff, loading, needsVerification, signIn, signUp, signOut, resendVerification, refresh } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [verifySent, setVerifySent] = useState(false);

  if (!firebaseReady) {
    return (
      <Shell>
        <div className="flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-200">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Firebase isn't connected yet.</p>
            <p className="mt-1 text-amber-200/80">
              Paste your web app config into <code className="rounded bg-black/30 px-1">src/lib/firebase-config.ts</code>.
              The file itself contains step-by-step instructions (about 2 minutes).
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (loading) {
    return <Shell><p className="text-center text-sm text-zinc-500">Loading…</p></Shell>;
  }

  if (user && isStaff) return <Navigate to="/admin" replace />;

  if (user && needsVerification) {
    return (
      <Shell>
        <div className="text-center">
          <MailCheck size={40} className="mx-auto text-accent-400" />
          <h2 className="mt-4 font-display text-lg font-bold text-white">Verify your email</h2>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            We sent a verification link to <span className="text-white">{user.email}</span>. Admin
            access unlocks as soon as it's verified — click the link, then come back here.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button className="btn-primary" onClick={() => refresh()}>I've verified — check again</button>
            <button
              className="btn-ghost text-sm"
              onClick={async () => { await resendVerification(); setVerifySent(true); }}
            >
              {verifySent ? 'Sent! Check your inbox (and spam)' : 'Resend verification email'}
            </button>
            <button className="text-xs text-zinc-500 hover:text-zinc-300" onClick={() => signOut()}>Sign out</button>
          </div>
        </div>
      </Shell>
    );
  }

  if (user && !isStaff) {
    return (
      <Shell>
        <div className="text-center">
          <LockKeyhole size={40} className="mx-auto text-zinc-500" />
          <h2 className="mt-4 font-display text-lg font-bold text-white">No staff access</h2>
          <p className="mt-2 text-sm text-zinc-400">
            The account <span className="text-white">{user.email}</span> isn't on the team yet.
            Ask the owner to add you in Admin → Team, then sign in again.
          </p>
          <button className="btn-ghost mt-6" onClick={() => signOut()}>Sign out</button>
        </div>
      </Shell>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'signin') await signIn(email, password);
      else await signUp('', email, '', password);
    } catch (err: any) {
      const code: string = err?.code ?? '';
      setError(
        code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')
          ? 'Wrong email or password.'
          : code.includes('email-already-in-use')
            ? 'That email already has an account — sign in instead.'
            : code.includes('weak-password')
              ? 'Password must be at least 6 characters.'
              : 'Sign-in failed. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        </div>
        <div>
          <label className="label">Password</label>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} />
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button className="btn-primary w-full" disabled={busy}>
          {busy ? 'Working…' : mode === 'signin' ? 'Sign In' : 'Create Account'}
        </button>
        <button
          type="button"
          className="w-full text-center text-sm text-zinc-500 hover:text-zinc-300"
          onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); }}
        >
          {mode === 'signin' ? 'First time? Create the owner account' : 'Already have an account? Sign in'}
        </button>
        {mode === 'signup' && (
          <p className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs leading-relaxed text-zinc-400">
            Use <span className="text-white">{ADMIN_EMAILS[0]}</span> to get owner access
            automatically after email verification.
          </p>
        )}
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-night-950 p-6">
      <div className="hero-grid pointer-events-none absolute inset-0" />
      <div className="relative w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo size={40} /></div>
        <div className="card bg-night-900/80 p-7">
          <h1 className="mb-5 text-center font-display text-lg font-bold text-white">Admin Panel</h1>
          {children}
        </div>
        <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 text-sm text-zinc-500 transition-colors hover:text-zinc-300">
          <ArrowLeft size={14} /> Back to website
        </Link>
      </div>
    </div>
  );
}
